import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, ShieldAlert, ArrowLeft, Lock, Fingerprint, Timer } from "lucide-react";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import { VaultSetupWizard, VaultUnlockV2 } from "@/components/dashboard/VaultSetupV2";

const HEARTBEAT_MS = 20000;   // poll the server session every 20s
const IDLE_MS = 300000;       // relock locally after 5 min of no activity

/* Loading shim while we resolve the server-side authorization gate. */
function VaultChecking() {
  return (
    <div className="absolute inset-0 z-30 bg-charcoal grid place-items-center" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-checking">
      <div className="flex flex-col items-center gap-3">
        <Loader2 size={22} className="text-gold animate-spin" />
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/45">Verifying clearance</p>
      </div>
    </div>
  );
}

/* HARD ACCESS WALL — shown to every non-founder operator. Fail-closed, no
   onboarding, no internals, no soft "coming soon" treatment. Neo-Brutalist. */
export function VaultRestricted({ onBack, handle }) {
  useEffect(() => {  // hide the floating PiP so it can't block the exit CTA on mobile
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);
  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-restricted">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-2 border-gold/20">
        <button onClick={onBack} data-testid="vault-restricted-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} className="text-gold" />
        </button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Secure Vault · Gateway</p>
          <h1 className="font-serif text-2xl text-white">Access Control</h1>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
        <span className="relative w-24 h-24 rounded-[28px] border-4 border-red-500 bg-black grid place-items-center shadow-[5px_5px_0px_#000]">
          <ShieldAlert size={46} className="text-red-500" />
          <span className="absolute -bottom-2 -right-2 w-9 h-9 rounded-2xl border-2 border-gold bg-charcoal grid place-items-center">
            <Lock size={16} className="text-gold" />
          </span>
        </span>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-red-400" data-testid="vault-restricted-code">Access Denied · 403</p>
          <h2 className="mt-2 font-serif text-3xl text-white leading-tight">Restricted Vault</h2>
        </div>

        <div className="w-full max-w-[300px] border-2 border-white/12 rounded-2xl bg-white/5 px-4 py-3">
          <p className="font-mono text-[11px] text-white/60 leading-relaxed">
            This secure workspace is limited to <span className="text-gold font-black">authorized operators only</span>. Your profile does not hold clearance to enter the vault internals.
          </p>
        </div>

        <div className="w-full max-w-[300px] flex items-center gap-2 rounded-xl border-2 border-dashed border-gold/30 px-3 py-2">
          <Fingerprint size={14} className="text-gold/70 shrink-0" />
          <p className="font-mono text-[9px] text-white/45 leading-snug text-left">
            Signed in as <span className="text-white/70">@{(handle || "operator").toLowerCase()}</span> — all access attempts are logged.
          </p>
        </div>

        <button onClick={onBack} data-testid="vault-restricted-exit" className="w-full max-w-[300px] bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none flex items-center justify-center gap-2">
          <ArrowLeft size={15} /> Return to Command Center
        </button>
      </div>
    </div>
  );
}

/* HARD LOCKOUT SCREEN — after consecutive wrong-PIN attempts. Dynamic countdown;
   the gateway is unreachable until the timer hits zero. Neo-Brutalist. */
export function VaultLockout({ seconds, onExpire, onBack }) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {  // keep the floating PiP off the lockout controls
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);
  useEffect(() => { setLeft(seconds); }, [seconds]);
  useEffect(() => {
    if (left <= 0) { onExpire?.(); return; }
    const t = setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [left, onExpire]);

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-lockout">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-2 border-gold/20">
        <button onClick={onBack} data-testid="vault-lockout-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} className="text-gold" />
        </button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Secure Vault · Gateway</p>
          <h1 className="font-serif text-2xl text-white">Temporarily Locked</h1>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
        <span className="w-24 h-24 rounded-[28px] border-4 border-red-500 bg-black grid place-items-center shadow-[5px_5px_0px_#000]">
          <Timer size={44} className="text-red-500" />
        </span>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-red-400">Too Many Attempts</p>
          <h2 className="mt-2 font-serif text-3xl text-white leading-tight">Cooldown Active</h2>
        </div>
        <div className="w-full max-w-[280px] border-2 border-gold/50 rounded-2xl bg-white/5 px-4 py-4 shadow-[3px_3px_0px_#000]">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/45">Retry available in</p>
          <p className="mt-1 font-mono text-5xl font-black text-gold tabular-nums" data-testid="vault-lockout-timer">{mm}:{ss}</p>
        </div>
        <p className="font-mono text-[10px] text-white/50 max-w-[280px] leading-relaxed">
          The vault gateway is sealed after repeated incorrect PIN entries. Please wait for the countdown to finish before trying again.
        </p>
      </div>
    </div>
  );
}

/* ================= CANONICAL VAULT UNLOCK CONTROLLER =====================
   Single source of truth for the vault security state machine:
     1. checking      → resolve the server-side founder authorization gate
     2. restricted    → non-founder hard wall (fail closed, no internals)
     3. lockout       → wrong-PIN cooldown countdown (gateway sealed)
     4. setup         → founder with NO PIN → force PIN creation first
     5. gateway       → founder with a PIN → biometric-first → PIN fallback
     6. unlocked      → render vault internals (children) + heartbeat/relock
   Vault internals are NEVER mounted (and thus never fetch) before unlock. */
export default function VaultGate({ user, vaultMode, unlocked, onUnlock, onOnboarded, onRelock, onBack, children }) {
  const [status, setStatus] = useState(null); // null = still checking; else setup-status payload
  const [lockSecs, setLockSecs] = useState(0);
  const [session, setSession] = useState(null);
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const loadStatus = useCallback(async () => {
    const s = await vaultSecurityApi.setupStatus();
    setStatus(s);
    if (s.armed && s.methods?.pin) {
      try { const lk = await vaultSecurityApi.sessionLockout(); if (lk.locked) setLockSecs(lk.seconds_remaining || 0); } catch { /* non-blocking */ }
    }
    return s;
  }, []);

  // Resolve the setup/unlock state machine on mount (also lazily resets legacy creds).
  useEffect(() => {
    let live = true;
    loadStatus().catch(() => { if (live) setStatus({ armed: false, methods: {}, count: 0, min_required: 3, enrolled_order: [] }); });
    return () => { live = false; };
  }, [loadStatus]);

  const doRelock = useCallback(() => {
    const sid = sessionRef.current;
    if (sid) { vaultSecurityApi.sessionClose(sid).catch(() => {}); sessionRef.current = null; }
    setSession(null);
    onRelock?.();
  }, [onRelock]);

  // Open the server-side session, then flip the parent unlock flag.
  const handleUnlock = useCallback(async () => {
    try { const r = await vaultSecurityApi.sessionOpen(); setSession(r.session_id); } catch { /* fail-soft */ }
    onUnlock();
  }, [onUnlock]);

  const handleOnboarded = useCallback(async () => {
    try { const r = await vaultSecurityApi.sessionOpen(); setSession(r.session_id); } catch { /* fail-soft */ }
    onOnboarded();
  }, [onOnboarded]);

  // While unlocked: idle detection + server heartbeat → auto-relock on idle,
  // token change (re-login), manual revoke, or absolute timeout.
  useEffect(() => {
    if (!unlocked || !session) return;
    let lastActivity = Date.now();
    const bump = () => { lastActivity = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(async () => {
      const idleMs = Date.now() - lastActivity;
      if (idleMs > IDLE_MS) { doRelock(); return; }
      try {
        const r = await vaultSecurityApi.sessionHeartbeat(session, idleMs < HEARTBEAT_MS);
        if (!r.valid) doRelock();
      } catch { /* transient network — keep the session, retry next beat */ }
    }, HEARTBEAT_MS);
    return () => { clearInterval(iv); events.forEach((e) => window.removeEventListener(e, bump)); };
  }, [unlocked, session, doRelock]);

  // Best-effort revoke when the gate unmounts (navigating away closes the vault).
  useEffect(() => () => {
    const sid = sessionRef.current;
    if (sid) vaultSecurityApi.sessionClose(sid).catch(() => {});
  }, []);

  if (unlocked) return children;
  if (status === null) return <VaultChecking />;
  if (lockSecs > 0) return <VaultLockout seconds={lockSecs} onExpire={() => setLockSecs(0)} onBack={onBack} />;
  if (!status.armed) return <VaultSetupWizard status={status} onRefresh={loadStatus} onArmed={handleOnboarded} onBack={onBack} />;
  return <VaultUnlockV2 status={status} onUnlock={handleUnlock} onBack={onBack} onLockout={(s) => setLockSecs(s)} />;
}
